import { AppState } from '../../../../app.reducers';
import { Store, select } from '@ngrx/store';
import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { UserProfile } from '../../../user/models';
import { getUserProfile } from '../../../user/redux/user.selectors';
import { UpdateUserProfileAction } from '../../../user/redux/user.actions';
import { AccountService, CustomValidators } from '../../../../shared';
import { SubSink } from 'subsink';
import { ToastrService } from 'ngx-toastr';
import { getAuthState } from 'app/shared/auth/redux/auth.selectors';
import { AuthState } from 'app/shared/auth/redux/auth.state';

@Component({
  templateUrl: './admin-settings.component.html',
  styleUrls: ['./admin-settings.component.scss'],
  standalone: false
})
export class AdminSettingsComponent implements OnInit, OnDestroy {

  @ViewChild('currentPassword', { static: true }) currentPasswordInput;
  showTypingText = 'show typing';

  feedbackMessages: any[] = [];
  successMessage = '';
  pageForm: UntypedFormGroup;
  isSubmitting = false;
  image: any;
  userProfileForm: UntypedFormGroup;
  firstName: UntypedFormControl = new UntypedFormControl();
  lastName: UntypedFormControl = new UntypedFormControl();
  phone: UntypedFormControl = new UntypedFormControl();
  email: UntypedFormControl = new UntypedFormControl();
  joinedOn: Date;

  userProfile: Observable<UserProfile>;
  private subs = new SubSink();

  constructor(private router: Router,
    private store: Store<AppState>,
    private _accountService: AccountService,
    private toastr: ToastrService) {
  }

  ngOnInit() {
    this.initForm();
    this.initForms();

    this.subs.sink = this.store
      .select(getAuthState)
      .subscribe((authState: AuthState) => {
        if (authState.loggedIn !== true) {
          this.goToMain();
        }
      });

    this.userProfile = this.store.pipe(select(getUserProfile));
    this.subs.sink = this.userProfile
      .subscribe((profile) => {
        this.updateFormValues(profile);
      });
  }

  updateFormValues(userProfile: UserProfile) {

    if (userProfile == null) {
      return;
    }

    this.joinedOn = userProfile.joinedOn;

    this.userProfileForm.controls['firstName'].setValue(userProfile.firstName);
    this.userProfileForm.controls['lastName'].setValue(userProfile.lastName);
    this.userProfileForm.controls['phone'].setValue(userProfile.phone);
    this.userProfileForm.controls['email'].setValue(userProfile.email);
  }

  initForm() {
    this.userProfileForm = new UntypedFormGroup({
      firstName: this.firstName,
      lastName: this.lastName,
      phone: this.phone,
      email: new UntypedFormControl(this.email, [Validators.required, CustomValidators.emailValidator, Validators.maxLength(100)]),
      fileUpload: new UntypedFormControl()
    });
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  saveProfile() {
    const firstName = this.userProfileForm.value.firstName.trim();
    const lastName = this.userProfileForm.value.lastName.trim();
    const email = this.userProfileForm.value.email;
    const phone = this.userProfileForm.value.phone;

    const profile = new UserProfile();
    profile.firstName = firstName;
    profile.lastName = lastName;
    profile.email = email;
    profile.phone = phone;

    this.store.dispatch(new UpdateUserProfileAction(profile));    
  }

  onCancel() {
    this.router.navigate(['/useraccount/admin/settings']);    
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  showCurrentPassword(e) {

    const inputType =
      this.currentPasswordInput.nativeElement.type;

    if (inputType === 'password') {
      this.currentPasswordInput.nativeElement.type = 'text';
      this.showTypingText = 'hide typing';
    } else {
      this.currentPasswordInput.nativeElement.type = 'password';
      this.showTypingText = 'show typing';
    }
  }

  onChangePassword() {
    this.isSubmitting = true;
    this.subs.sink = this.changePassword()
      .subscribe(() => {
        this.pageForm.reset();

        this.isSubmitting = false;

        this.toastr.success('Password Changed');
        this.goToMain();

      }, (error) => this.handleError(error));
  }

  changePassword() {
    this.successMessage = '';
    this.feedbackMessages = [];

    const payload = {
      email: this.pageForm.value.email,
      currentPassword: this.pageForm.value.currentPassword,
      newPassword: this.pageForm.value.newPassword,
      confirmPassword: this.pageForm.value.confirmPassword,
    };

    return this._accountService.changePassword(payload);
  }

  private goToMain() {
    this.router.navigate(['/useraccount/admin/settings']);
  }

  private initForms() {
    this.pageForm = new UntypedFormGroup({
      email: new UntypedFormControl(),
      currentPassword: new UntypedFormControl(),
      newPassword: new UntypedFormControl(),
      confirmPassword: new UntypedFormControl()
    });

    const newPassword = this.pageForm.controls['newPassword'];
    const confirmPassword = this.pageForm.controls['confirmPassword'];

    newPassword.setValidators(Validators.compose([Validators.required, CustomValidators.passwordPolicy(newPassword)]));

    confirmPassword.setValidators(Validators.compose([Validators.required, CustomValidators.equal(newPassword)]));

    this.subs.sink = newPassword.valueChanges.subscribe(() => confirmPassword.updateValueAndValidity());
  }

  private handleError(errors) {
    this.isSubmitting = false;
    this.feedbackMessages = errors;
  }
}
